import React, { MutableRefObject } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { makeTransitionStyles } from '../styles';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';
import T from 'i18n-react';
import withStyles from '@material-ui/core/styles/withStyles';
import indigo from '@material-ui/core/colors/indigo';
import MuiButton from '@material-ui/core/Button';

const TextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ReadMoreButton = withStyles((theme) => ({
  root: {
    fontSize: (props) => theme.typography.pxToRem(props.fontSize || 14),
    color: indigo[600],
    padding: 0,
    '&:hover': {
      color: indigo[600],
      backgroundColor: 'transparent',
    },
  },
}))(({ fontSize, ...props }) => (
  <MuiButton
    disableRipple
    disableFocusRipple
    disableTouchRipple
    {...props}
    component="a"
  />
));

const useStyles = makeStyles((theme) => ({
  expand: {
    height: `calc(${theme.typography.body1.fontSize} * ${theme.typography.body1.lineHeight} * 2)`,
    overflow: 'hidden',
    ...makeTransitionStyles('height .2s'),
  },
  more: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(1),
  },
  cancelDateTypo: {
    marginLeft: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      fontSize: 14,
      marginLeft: theme.spacing(2),
    },
  },
}));

const ExpansionText = ({ canceledAt, children }) => {
  const classes = useStyles();

  const [readMore, setReadMore] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [width, setWidth] = React.useState(window.innerWidth);

  const textRef: MutableRefObject<HTMLElement> = React.useRef();
  const isClamped = () =>
    !!textRef.current &&
    textRef.current.scrollHeight > textRef.current.clientHeight;

  const handleExpandStyles = (expanded) => {
    const textElem = textRef.current;
    // TODO CSSのハンドリングはuseStylesにオブジェクトを渡すことで解決するので、DOMを直接操作しないやり方で実装する
    if (textElem) {
      textElem.style.removeProperty('height');
      if (expanded) {
        textElem.style.removeProperty('white-space');
      } else {
        textElem.style.whiteSpace = 'pre-wrap';
        textElem.style.height = `${textRef.current.scrollHeight}px`;
      }
    }
  };

  const handleExpand = () => {
    setExpanded((prevState) => {
      handleExpandStyles(prevState);
      return !prevState;
    });
  };

  const handleResize = () => setWidth(window.innerWidth);

  React.useEffect(() => {
    setReadMore(isClamped());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const textElem = textRef.current;
    if (textElem) {
      textElem.style.removeProperty('height');
      textElem.style.removeProperty('white-space');
    }
    setReadMore(isClamped());
    if (expanded) {
      setExpanded(false);
      handleExpandStyles(true);
    }
  }, [width]);

  return (
    <div>
      <TextWrapper>
        <Typography
          className={clsx(
            'text-break-all',
            classes.expand,
            !expanded && 'line-truncate-2',
          )}
          ref={textRef}
        >
          {children}
        </Typography>
        {canceledAt && (
          <Typography
            color="textSecondary"
            classes={{ root: classes.cancelDateTypo }}
          >
            {moment(canceledAt).format(T.translate('moment.date'))}
          </Typography>
        )}
      </TextWrapper>
      {readMore && (
        <div className={classes.more}>
          <ReadMoreButton onClick={handleExpand} fontSize={12}>
            {T.translate(expanded ? 'close' : 'displayMore')}
          </ReadMoreButton>
        </div>
      )}
    </div>
  );
};

export default ExpansionText;
